import { Constants } from "@constants";
import { createSlice } from "@reduxjs/toolkit";
import apiService from "@utils/apiService";
import { verifyApiResponse } from "@utils/apiResponseHelper";

const initialState = {
  loading: false,
  useCasesInfo: {},
  success: false,
  error: false,
  errorMessage: "",
  updateOrGetUseCasesLoading: false,
  updateOrGetUseCasesError: false,
  deleteImportedTemplateLoading: false,
  deleteImportedTemplateSuccess: false,
  deleteImportedTemplateError: false,
  enableNextBtn: false,
  scoresForUseCases: [],
  commitImpoetedUseCasesScoresLoading: false,
  commitImpoetedUseCasesScoresSuccess: false,
  commitImpoetedUseCasesScoresError: false,
  isTaskInProgress: false,
};

export const uploadUseCasesSlice = createSlice({
  name: "uploadUseCases",
  initialState,
  reducers: {
    uploadUseCasesStart: (state) => ({
      ...state,
      loading: true,
      error: false,
      errorMessage: "",
    }),
    uploadUseCasesSuccess: (state, action) => ({
      ...state,
      loading: false,
      useCasesInfo: action.payload,
      isTaskInProgress: true,
      success: true,
      error: false,
      updateOrGetUseCasesLoading: false,
      updateOrGetUseCasesError: false,
    }),
    uploadUseCasesFailure: (state, action) => ({
      ...state,
      loading: false,
      error: true,
      errorMessage: action?.payload?.message || ''
    }),
    updateOrGetUseCasesStart: (state) => ({
      ...state,
      updateOrGetUseCasesLoading: true,
    }),
    updateOrGetUseCasesFailure: (state) => ({
      ...state,
      updateOrGetUseCasesError: true,
    }),
    deleteImportedTemplateStart: (state) => ({
      ...state,
      deleteImportedTemplateLoading: true,
    }),
    deleteImportedTemplateSuccess: (state) => ({
      ...state,
      deleteImportedTemplateLoading: false,
      deleteImportedTemplateSuccess: true,
      deleteImportedTemplateError: false,
      isTaskInProgress: false,
    }),
    resetDeleteImportedTemplate: (state) => ({
      ...state,
      deleteImportedTemplateLoading: false,
      deleteImportedTemplateSuccess: false,
      deleteImportedTemplateError: false
    }),
    deleteImportedTemplateFailure: (state) => ({
      ...state,
      deleteImportedTemplateError: true,
    }),
    setEnableNextBtn: (state, action) => ({
      ...state,
      enableNextBtn: action.payload,
    }),
    updateScoresForUseCases: (state, action) => ({
      ...state,
      scoresForUseCases: action.payload,
    }),
    commitImpoetedUseCasesScoresStart: (state) => ({
      ...state,
      commitImpoetedUseCasesScoresLoading: true,
    }),
    commitImpoetedUseCasesScoresSuccess: (state) => ({
      ...state,
      commitImpoetedUseCasesScoresLoading: false,
      commitImpoetedUseCasesScoresSuccess: true,
      commitImpoetedUseCasesScoresError: false,
      isTaskInProgress: false,
    }),
    commitImpoetedUseCasesScoresFailure: (state) => ({
      ...state,
      commitImpoetedUseCasesScoresError: true,
    }),
    resetCommitImpoetedUseCasesScores: (state) => ({
      ...state,
      commitImpoetedUseCasesScoresLoading: false,
      commitImpoetedUseCasesScoresSuccess: false,
      commitImpoetedUseCasesScoresError: false
    }),
    resetUploadUseCases: () => ({
      ...initialState,
    })
  },
});

const { actions, reducer } = uploadUseCasesSlice;

export const { 
  uploadUseCasesStart,
  uploadUseCasesSuccess,
  uploadUseCasesFailure,
  resetUploadUseCases,
  updateOrGetUseCasesStart,
  updateOrGetUseCasesFailure,
  deleteImportedTemplateStart,
  deleteImportedTemplateSuccess,
  deleteImportedTemplateFailure,
  resetDeleteImportedTemplate,
  setEnableNextBtn,
  updateScoresForUseCases,
  commitImpoetedUseCasesScoresStart,
  commitImpoetedUseCasesScoresSuccess,
  commitImpoetedUseCasesScoresFailure,
  resetCommitImpoetedUseCasesScores,
} = actions;

export const uploadUseCasesTemplate = (fileObject, scorecardId) => (dispatch) => {
  dispatch(uploadUseCasesStart());
  apiService
    .post(`${Constants.API.SCORE_CARD}/${scorecardId}/${Constants.API.IMPORT_USE_CASES.UPLOAD}`, fileObject)
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success && Object.keys(data?.data).length) {
        dispatch(uploadUseCasesSuccess(data?.data));
      } else {
        dispatch(uploadUseCasesFailure());
      }
    })
    .catch((error) => {
      if (error?.response?.status === 400) {
        dispatch(uploadUseCasesFailure({
          message : error?.response?.data?.systemMessage?.message
        }));
      } else {
        dispatch(uploadUseCasesFailure());
      }
    });
}

export const getImportedUseCases = (payload) => (dispatch) => {
  dispatch(updateOrGetUseCasesStart())
  apiService
    .get(`${Constants.API.SCORE_CARD}/${payload?.scorecardId}/${Constants.API.IMPORT_USE_CASES.UPLOAD}/${payload?.importId}`)
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success && Object.keys(data?.data).length) {
        dispatch(uploadUseCasesSuccess(data?.data));
      } else {
        dispatch(updateOrGetUseCasesFailure());
      }
    })
    .catch(() => {
      dispatch(updateOrGetUseCasesFailure());
    });
}

export const updateSimilarUseCases = (payload, resolutionObject) => (dispatch) => {
  dispatch(updateOrGetUseCasesStart())
  apiService
    .put(
      `${Constants.API.SCORE_CARD}/${payload?.scorecardId}/${Constants.API.IMPORT_USE_CASES.UPLOAD}/${payload?.importId}/${Constants.API.IMPORT_USE_CASES.ROW}/${payload?.rowId}`,
      resolutionObject,
    )    
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success && Object.keys(data?.data).length) {
        dispatch(uploadUseCasesSuccess(data?.data));
      } else {
        dispatch(updateOrGetUseCasesFailure());
      }
    })
    .catch(() => {
      dispatch(updateOrGetUseCasesFailure());
    });
}

export const deleteImportedTemplate = (payload) => (dispatch) => {
  dispatch(deleteImportedTemplateStart())
  apiService
    .delete(`${Constants.API.SCORE_CARD}/${payload?.scorecardId}/${Constants.API.IMPORT_USE_CASES.UPLOAD}/${payload?.importId}`)    
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success) {
        dispatch(deleteImportedTemplateSuccess());
      } else {
        dispatch(deleteImportedTemplateFailure());
      }
    })
    .catch(() => {
      dispatch(deleteImportedTemplateFailure());
    });
}

export const commitImpoetedUseCasesScores = (payload, scores) => (dispatch) => {
  dispatch(commitImpoetedUseCasesScoresStart())
  apiService
    .post(
      `${Constants.API.SCORE_CARD}/${payload?.scorecardId}/${Constants.API.IMPORT_USE_CASES.UPLOAD}/${payload?.importId}/${Constants.API.IMPORT_USE_CASES.COMMIT}`,
      scores
    )    
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success) {
        dispatch(commitImpoetedUseCasesScoresSuccess());
      } else {
        dispatch(commitImpoetedUseCasesScoresFailure());
      }
    })
    .catch(() => {
      dispatch(commitImpoetedUseCasesScoresFailure());
    });
}

export const updateOverallScoreForUseCase = (payload, scoreObject) => (dispatch, getState) => {
  dispatch(updateOrGetUseCasesStart())
  apiService
    .put(
      `${Constants.API.SCORE_CARD}/${payload?.scorecardId}/${Constants.API.IMPORT_USE_CASES.UPLOAD}/${payload?.importId}/${Constants.API.IMPORT_USE_CASES.ROW}/${payload?.rowId}/${Constants.API.IMPORT_USE_CASES.SCORE}`,
      scoreObject,
    )    
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success && Object.keys(data?.data).length) {
        const currData = data?.data;
        const state = getState();
        const updatedScoresForUseCases = state.uploadUseCases.scoresForUseCases.map((row) => {
          if (row?.rowId === currData?.id) {
            return {
              ...row,
              businessValueRating: currData.businessValueRating,
              feasibilityRating: currData.feasibilityRating,
              ...currData.scores,
            };
          }
          return row;
        });
        dispatch(updateScoresForUseCases(updatedScoresForUseCases));
      } else {
        dispatch(updateOrGetUseCasesFailure());
      }
    })
    .catch(() => {
      dispatch(updateOrGetUseCasesFailure());
    });
}

export default reducer;
